import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import PhoneInput from '@/components/PhoneInput'
import { Button, Modal, Form, Input, DatePicker, InputNumber, Divider, message, Card, Row, Col, Tag, Space, Collapse, Spin, Empty, Select, Tooltip, Upload, Popconfirm, Table, Alert, Pagination, theme, Typography, Tabs } from 'antd'
import { DeleteOutlined, DownloadOutlined, UploadOutlined, PlusOutlined, ZoomInOutlined, ZoomOutOutlined, RobotOutlined, ToolOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { AutoRefreshBadge } from '@/components/AutoRefreshBadge'
import { MarketSearch, OfferPreviewBanner } from '@/components/MarketSearch'
import type { SearchResult } from '@/components/MarketSearch'
import dayjs from 'dayjs'
import axiosInstance from '@/lib/axios'
import { useWorkOrders } from '@/hooks/useWorkOrders'
import { useRdas } from '@/hooks/useRdas'
import { useSuppliers } from '@/hooks/useSuppliers'
import { usePermission } from '@/hooks/usePermission'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useWOStatusTypesList } from '@/hooks/useSystemTables'
import { formatEuro } from '@/utils/formatters'
import type { WorkOrder, Intervention, PurchaseRequest } from '@/types'
import './PurchaseRequestsPage.css'
import RDAStateTransition from '@/components/RDAStateTransition'
import { TagArticoliResi } from '@/components/TagArticoliResi'


interface RDAItem {
  id?: number
  descrizione: string
  quantita: number
  prezzo_listino?: number
  prezzo_unitario: number
  sconto_percentuale: number
  iva_percentuale: number
  costo_spedizione: number
  selectedOffer?: SearchResult
}

const getStatusDisplay = (statusId: number, statusNome?: string | null) => {
  return { nome: statusNome ?? `Stato ${statusId}`, color: 'blue' }
}

const RDO_STATUS_COLORS: Record<string, string> = {
  'bozza': 'default',
  'in attesa approvazione': 'processing',
  'approvata': 'success',
  'ordinata': 'geekblue',
  'ricevuta': 'cyan',
  'rifiutata': 'error',
  'annullata': 'warning',
}

const RDO_STATUS_LABELS: Record<string, string> = {
  'bozza': 'Bozza',
  'in attesa approvazione': 'In Attesa',
  'approvata': 'Approvata',
  'ordinata': 'Ordinata',
  'ricevuta': 'Ricevuta',
  'rifiutata': 'Rifiutata',
  'annullata': 'Annullata',
}

const RDO_STATUS_ORDER = ['bozza', 'in attesa approvazione', 'approvata', 'ordinata', 'ricevuta', 'rifiutata', 'annullata']

const WO_STATUS_COLORS: Record<string, string> = {
  'accettazione': 'default',
  'in analisi': 'blue',
  'in approvazione': 'orange',
  'approvata': 'lime',
  'in lavorazione': 'gold',
  'completata': 'green',
  'riconsegnata': 'cyan',
  'sospesa': 'warning',
  'annullata': 'error',
}

const LabeledValue: React.FC<{ label: string; value?: string | null; accent?: boolean; mono?: boolean }> = ({ label, value, accent, mono }) => {
  const { token: t } = theme.useToken()
  return (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 700, color: t.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.6px', lineHeight: 1, marginBottom: '2px' }}>
        {label}
      </div>
      <div style={{
        fontSize: '13px',
        fontWeight: accent ? 600 : 400,
        color: accent ? t.colorPrimary : t.colorText,
        fontFamily: mono ? 'monospace' : 'inherit',
        letterSpacing: mono ? '0.04em' : 'inherit',
        lineHeight: 1.3,
      }}>
        {value || '—'}
      </div>
    </div>
  )
}

const PurchaseRequestsPage = () => {
  const { token } = theme.useToken()
  const { setTitle } = usePageTitle()
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoExpandWoId: number | undefined = (location.state as any)?.expandWorkOrderId
  const rdaStatoFiltro = searchParams.get('stato_nome') || undefined

  const [isRdaModalOpen, setIsRdaModalOpen] = useState(false)
  const [editingRda, setEditingRda] = useState<PurchaseRequest | null>(null)
  const [rdaForm] = Form.useForm()
  const [expandedKeys, setExpandedKeys] = useState<string[]>(
    autoExpandWoId ? [String(autoExpandWoId)] : []
  )
  const [workOrderDetails, setWorkOrderDetails] = useState<Map<number, WorkOrder>>(new Map())
  const [workOrderRdas, setWorkOrderRdas] = useState<Map<number, any[]>>(new Map())
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set())
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false)
  const [rdaItems, setRdaItems] = useState<RDAItem[]>([])
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [isViewingAttachmentModal, setIsViewingAttachmentModal] = useState(false)
  const [attachmentBlobUrl, setAttachmentBlobUrl] = useState<string>('')
  const [attachmentZoom, setAttachmentZoom] = useState<number>(100)
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState<string>('')

  const [activeTab, setActiveTab] = useState<'in-lavorazione' | 'riconsegnate'>('in-lavorazione')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [woStatoFiltro, setWoStatoFiltro] = useState<string | undefined>(undefined)
  const [rdaStatoLocal, setRdaStatoLocal] = useState<string | undefined>(rdaStatoFiltro)

  const [isSubmitApprovalModalOpen, setIsSubmitApprovalModalOpen] = useState(false)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [aiPreviewData, setAiPreviewData] = useState<any | null>(null)
  const [isAiPreviewModalOpen, setIsAiPreviewModalOpen] = useState(false)
  const [aiSupplierResolved, setAiSupplierResolved] = useState<string | null>(null)
  const [supplierModalInitialNome, setSupplierModalInitialNome] = useState<string>('')

  useEffect(() => {
    setTitle('Gestione Richieste di Offerta - RDO')
  }, [setTitle])

  // Autorizzazioni
  const canApproveRda = usePermission('purchase_requests.approve')
  const canViewSuppliers = usePermission('suppliers.view')
  const showFormErrorMessage = editingRda?.purchase_request_status_id !== 1 && !canApproveRda
  const isFieldsDisabled = showFormErrorMessage

  const { data: workOrdersData, isLoading: workOrdersLoading, dataUpdatedAt: woUpdatedAt } = useWorkOrders(page, pageSize, searchTerm || undefined, woStatoFiltro, undefined, undefined, undefined, undefined, rdaStatoLocal ?? 'all', undefined, true, activeTab === 'riconsegnate')
  const { data: woStatusTypes = [] } = useWOStatusTypesList()
  const { createAsync: createRda, updateAsync: updateRda, deleteAsync: deleteRda } = useRdas()
  const { data: suppliersData, refetch: refetchSuppliers } = useSuppliers(1, 100, undefined, undefined, { enabled: canViewSuppliers })

  // Carica i dettagli di un work order (con interventi, RDO e RDA)
  const loadWorkOrderDetail = useCallback(async (woId: number) => {
    try {
      setLoadingDetails(prev => new Set(prev).add(woId))
      const [detailResponse, rdasResponse] = await Promise.all([
        axiosInstance.get(`/work-orders/${woId}`),
        axiosInstance.get(`/rdas/work-order/${woId}`).catch(() => ({ data: [] })),
      ])
      setWorkOrderDetails(prev => new Map(prev).set(woId, detailResponse.data))
      setWorkOrderRdas(prev => new Map(prev).set(woId, rdasResponse.data ?? []))
    } catch (error) {
      console.error(`❌ Errore nel caricamento work order ${woId}:`, error)
      message.error('Errore nel caricamento dei dettagli')
    } finally {
      setLoadingDetails(prev => {
        const newSet = new Set(prev)
        newSet.delete(woId)
        return newSet
      })
    }
  }, [])

  // Carica i dettagli della WO auto-espansa da navigazione dashboard
  useEffect(() => {
    if (autoExpandWoId) {
      loadWorkOrderDetail(autoExpandWoId)
    }
  }, [autoExpandWoId, loadWorkOrderDetail])

  // Precarica i dettagli di TUTTI i work orders per il filtro iniziale
  useEffect(() => {
    if (!workOrdersData?.items || workOrdersData.items.length === 0) return

    
    // Carica in parallelo, con limite per evitare too many requests
    const loadInBatches = async () => {
      const items = workOrdersData.items
      const batchSize = 5
      
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize)
        await Promise.all(batch.map(wo => loadWorkOrderDetail(wo.id)))
      }
    }
    
    loadInBatches()
  }, [workOrdersData?.items, loadWorkOrderDetail])

  // Gestisci apertura/chiusura accordion
  const handleCollapseChange = (keys: string | string[]) => {
    const keyArray = Array.isArray(keys) ? keys : [keys]
    setExpandedKeys(keyArray)

    // Carica i dettagli per i work order appena aperti (sempre fresh)
    keyArray.forEach(key => {
      const woId = parseInt(key)
      loadWorkOrderDetail(woId)
    })
  }

  // Quando la modale RDA si chiude, refetcha il work order corrente per mostrare gli ultimi dati
  useEffect(() => {
    if (!isRdaModalOpen && editingRda?.work_order_id) {
      loadWorkOrderDetail(editingRda.work_order_id)
      if (attachmentBlobUrl) {
        window.URL.revokeObjectURL(attachmentBlobUrl)
        setAttachmentBlobUrl('')
      }
    }
  }, [isRdaModalOpen, attachmentBlobUrl])

  // Cleanup quando si chiude la modale di visualizzazione allegato
  useEffect(() => {
    if (!isViewingAttachmentModal && attachmentBlobUrl) {
      URL.revokeObjectURL(attachmentBlobUrl)
      setAttachmentBlobUrl('')
      setAttachmentZoom(100)
    }
  }, [isViewingAttachmentModal])

  // Helper function per chiudere la modale RDA con cleanup
  const closeRdaModal = useCallback(() => {
    setIsRdaModalOpen(false)
    setIsSubmitApprovalModalOpen(false)
    rdaForm.resetFields()
    setRdaItems([])
    setEditingRda(null)
    setRejectReason('')
    setIsRejectModalOpen(false)
  }, [rdaForm])

  // Espandi tutti
  const expandAll = () => {
    if (!workOrdersData?.items) return
    const allKeys = workOrdersData.items.map(wo => wo.id.toString())
    setExpandedKeys(allKeys)
    allKeys.forEach(key => {
      const woId = parseInt(key)
      if (!workOrderDetails.has(woId)) {
        loadWorkOrderDetail(woId)
      }
    })
  }

  // Richiudi tutti
  const collapseAll = () => {
    setExpandedKeys([])
  }

  const handleEditRda = async (rda: PurchaseRequest) => {
    try {
      let rdaToEdit = rda
      
      // Se è una nuova RDA (id=0), non fare il fetch
      if (rda.id !== 0) {
        // CRITICAL: Load fresh RDA data from backend EVERY TIME for existing RDAs
        const response = await axiosInstance.get(`/purchase-requests/${rda.id}`)
        rdaToEdit = response.data
      }
      
      setEditingRda(rdaToEdit)
      setRdaItems(normalizeRdaItems(rdaToEdit.items || []))
      rdaForm.resetFields()
      rdaForm.setFieldsValue({
        fornitore: rdaToEdit.fornitore,
        data_richiesta: rdaToEdit.data_richiesta ? dayjs(rdaToEdit.data_richiesta) : undefined,
        data_approvazione: rdaToEdit.data_approvazione ? dayjs(rdaToEdit.data_approvazione) : undefined,
        data_ordine: rdaToEdit.data_ordine ? dayjs(rdaToEdit.data_ordine) : undefined,
        data_ricezione: rdaToEdit.data_ricezione ? dayjs(rdaToEdit.data_ricezione) : undefined,
        data_consegna_prevista: rdaToEdit.data_consegna_prevista ? dayjs(rdaToEdit.data_consegna_prevista) : undefined,
        note: rdaToEdit.note ?? '',
      })
      setIsRdaModalOpen(true)
    } catch (error) {
      console.error(`❌ Error loading RDA ${rda.id}:`, error)
      message.error('Errore nel caricamento dei dati della RDO')
    }
  }

  // ===== FUNZIONI MANIPOLAZIONE ITEMS =====
  const normalizeRdaItems = (items: any[]): RDAItem[] => {
    return items.map(item => {
      // Ricostruisce selectedOffer dai campi offerta_* salvati nel DB
      const selectedOffer: SearchResult | undefined =
        item.offerta_source_url
          ? ({
              id: 0,
              source: item.offerta_source || '',
              source_url: item.offerta_source_url,
              titolo: item.offerta_titolo || item.descrizione || '',
              fornitore: item.offerta_fornitore || '',
              prezzo: parseFloat(item.prezzo_unitario_stimato) || 0,
              valuta: 'EUR',
              disponibilita: '',
              spedizione: '',
              immagine_url: item.offerta_immagine_url || '',
              snippet: '',
              ai_rank: 0,
              ai_score: item.offerta_ai_score != null ? parseFloat(item.offerta_ai_score) : null,
              ai_note: item.offerta_ai_note || '',
              ai_recommended: false,
            } as SearchResult)
          : undefined
      return {
        id: item.id,
        descrizione: item.descrizione || '',
        quantita: item.quantita || 0,
        prezzo_unitario: item.prezzo_unitario_stimato || 0,
        sconto_percentuale: Math.min(100, Math.max(0, item.sconto_percentuale ? parseFloat(item.sconto_percentuale) : 0)),
        iva_percentuale: item.iva_percentuale ?? 0,
        costo_spedizione: item.costo_spedizione ? parseFloat(item.costo_spedizione) : 0,
        selectedOffer,
      }
    })
  }

  const itemsContainerRef = useRef<HTMLDivElement>(null)

  const addItem = () => {
    setRdaItems([
      {
        descrizione: '',
        quantita: 1,
        prezzo_unitario: 0,
        sconto_percentuale: 0,
        iva_percentuale: 0,
        costo_spedizione: 0,
      },
      ...rdaItems,
    ])
    // Scroll in cima — il nuovo item è il primo
    setTimeout(() => {
      if (itemsContainerRef.current) {
        itemsContainerRef.current.scrollTop = 0
      }
    }, 50)
  }

  const updateItem = (index: number, field: keyof RDAItem, value: any) => {
    const updated = [...rdaItems]
    if (updated[index]) {
      (updated[index] as any)[field] = value
      setRdaItems(updated)
    }
  }

  const removeItem = (index: number) => {
    setRdaItems(rdaItems.filter((_, i) => i !== index))
  }

  const calcTotaleListinoItem = (item: RDAItem) =>
    (item.quantita || 0) * (item.prezzo_unitario || 0)

  const calcTotaleScontatoItem = (item: RDAItem) =>
    calcTotaleListinoItem(item) * (1 - (item.sconto_percentuale || 0) / 100)

  const calcLineTotal = (item: RDAItem) =>
    calcTotaleScontatoItem(item) + (item.costo_spedizione || 0)

  const totaleListino = useMemo(() =>
    rdaItems.filter(Boolean).reduce((s, it) => s + calcTotaleListinoItem(it), 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rdaItems])

  const totaleScontatoBase = useMemo(() =>
    rdaItems.filter(Boolean).reduce((s, it) => s + calcTotaleScontatoItem(it), 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rdaItems])

  const totaleSped = useMemo(() =>
    rdaItems.filter(Boolean).reduce((s, it) => s + (it.costo_spedizione || 0), 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rdaItems])

  const totaleScontato = useMemo(() => totaleScontatoBase + totaleSped,
  [totaleScontatoBase, totaleSped])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const totaleStimato = totaleListino  // backward compat per save payload
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const importoSconto = 0

  // Ottieni la descrizione dell'intervento per il titolo della modal
  const getInterventionDescription = () => {
    if (!editingRda?.descrizione) {
      return null
    }
    
    // La descrizione della RDA è nel formato: "Richiesta acquisto per intervento: DESCRIZIONE INTERVENTO"
    // Estraiamo la parte dopo "intervento: "
    const match = editingRda.descrizione.match(/intervento:\s*(.+)$/i)
    if (match && match[1]) {
      return match[1].trim()
    }
    
    // Fallback: usa la valutazione_danno dal WorkOrder
    const workOrder = workOrderDetails.get(editingRda.work_order_id)
    if (workOrder?.valutazione_danno) {
      return workOrder.valutazione_danno
    }
    
    return null
  }

  // Funzioni per gestire gli allegati della RDA
  const handleUploadRdaFile = async (file: File) => {
    if (!editingRda) return false

    let rdaId = editingRda.id

    // Se è una nuova RDA (id=0), auto-salva prima per ottenere un ID
    if (!rdaId) {
      try {
        const formValues = rdaForm.getFieldsValue()
        const result = await axiosInstance.post('/purchase-requests/', {
          work_order_id: editingRda.work_order_id,
          intervention_id: editingRda.intervention_id || null,
          descrizione: formValues.descrizione || 'RDO',
          fornitore: formValues.fornitore || '',
          purchase_request_status_id: 1,
          items: rdaItems.map((item: any) => ({
            descrizione: item.descrizione,
            quantita: item.quantita,
            prezzo_unitario_stimato: item.prezzo_unitario,
            iva_percentuale: item.iva_percentuale || 0,
            costo_spedizione: item.costo_spedizione || 0,
            offerta_source: item.selectedOffer?.source ?? null,
            offerta_source_url: item.selectedOffer?.source_url ?? null,
            offerta_titolo: item.selectedOffer?.titolo ?? null,
            offerta_fornitore: item.selectedOffer?.fornitore ?? null,
            offerta_immagine_url: item.selectedOffer?.immagine_url ?? null,
            offerta_ai_score: item.selectedOffer?.ai_score ?? null,
            offerta_ai_note: item.selectedOffer?.ai_note ?? null,
          })),
        })
        rdaId = result.data.id
        setEditingRda(prev => prev ? { ...prev, id: rdaId!, codice: result.data.codice } : null)
        message.info('RDO salvata automaticamente per consentire l\'upload')
      } catch (error: any) {
        message.error('Impossibile salvare la RDO: ' + (error.response?.data?.detail || error.message))
        return false
      }
    }

    setIsUploadingFile(true)
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await axiosInstance.post(
        `/purchase-requests/${rdaId}/upload-attachment`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

      message.success('File caricato con successo')
      setEditingRda(prev => prev ? { ...prev, file_allegato_path: response.data.file_path || response.data.filename } : null)
      return false
    } catch (error: any) {
      message.error('Errore durante l\'upload: ' + (error.response?.data?.detail || error.message || 'Errore sconosciuto'))
      return false
    } finally {
      setIsUploadingFile(false)
    }
  }

  const handleDeleteRdaFile = async () => {
    if (!editingRda?.id) return

    try {
      await axiosInstance.delete(`/purchase-requests/${editingRda.id}/attachment`)
      message.success('File eliminato con successo')
      setEditingRda(prev => prev ? { ...prev, file_allegato_path: undefined } : null)
    } catch (error: any) {
      message.error('Errore: ' + (error.response?.data?.detail || error.message || 'Errore sconosciuto'))
    }
  }

  const handleAnalyzeWithAI = async () => {
    if (!editingRda?.id) return
    setIsAnalyzing(true)
    try {
      const response = await axiosInstance.post(`/purchase-requests/${editingRda.id}/analyze-attachment`)
      const data = response.data.data
      setAiPreviewData(data)
      // Verifica subito se il fornitore esiste nel DB
      const nomeAi = (data.fornitore || '').trim().toLowerCase()
      const match = (suppliersData?.items || []).find(
        s => s.nome.trim().toLowerCase() === nomeAi || s.nome.trim().toLowerCase().includes(nomeAi) || nomeAi.includes(s.nome.trim().toLowerCase())
      )
      setAiSupplierResolved(match ? match.nome : null)
      setIsAiPreviewModalOpen(true)
    } catch (error: any) {
      const status = error.response?.status
      const detail = error.response?.data?.detail || error.message || 'Errore sconosciuto'
      if (status === 402) {
        message.error({ content: `💳 ${detail}`, duration: 8 })
      } else if (status === 503) {
        message.warning({ content: `⏳ ${detail}`, duration: 6 })
      } else {
        message.error(`Errore analisi AI: ${detail}`)
      }
    } finally {
      setIsAnalyzing(false)
    }
  }

  // Converte valori numerici italiani (virgola decimale) in number JS
  const parseItalianNum = (v: any): number => {
    if (typeof v === 'number') return v
    const s = String(v ?? '').trim().replace(/\./g, '').replace(',', '.')
    return parseFloat(s) || 0
  }

  // Auto-mapping: trova la chiave che corrisponde al pattern ma NON è una colonna totale/importo
  const isTotaleKey = (k: string) => /import|total|imponib/i.test(k)
  const findColKey = (item: any, pattern: RegExp, excludeTotale = false): string | undefined =>
    Object.keys(item).find(k => pattern.test(k.toLowerCase()) && (!excludeTotale || !isTotaleKey(k)))

  const handleApplyAiData = () => {
    if (!aiPreviewData) return
    const newValues: any = {
      descrizione: aiPreviewData.descrizione || rdaForm.getFieldValue('descrizione'),
      fornitore: aiSupplierResolved || rdaForm.getFieldValue('fornitore'),
    }
    if (aiPreviewData.data_documento) {
      const parsed = dayjs(aiPreviewData.data_documento, 'YYYY-MM-DD', true)
      if (parsed.isValid()) newValues.data_richiesta = parsed
    }
    rdaForm.setFieldsValue(newValues)
    if (Array.isArray(aiPreviewData.items) && aiPreviewData.items.length > 0) {
      const colonne: string[] = aiPreviewData.colonne || Object.keys(aiPreviewData.items[0] || {})
      setRdaItems(aiPreviewData.items.map((item: any) => {
        const descrKey = findColKey(item, /descr|articol|prodott|voce|denominaz/)
        const prezKey  = findColKey(item, /prezzo|^p\.u\.$|costo\s*unit/, true)
        // 1) cerca per nome colonna; 2) cerca la prima colonna con numero piccolo (1-999) che non sia prezzo/totale/descr; 3) fallback posizionale
        const qtaKey   = findColKey(item, /quantit|qt[aà]|^q\.?[t]|^q$|^n\.?$|^nr\.?$|^pz\.?$|pezzi|^num|qty/)
                      ?? colonne.find(col => {
                           if (col === descrKey || col === prezKey || isTotaleKey(col)) return false
                           const v = Number(item[col])
                           return Number.isFinite(v) && v > 0 && v < 1000 && !String(item[col]).includes('.')
                         })
                      ?? colonne[1]
        const sconKey  = findColKey(item, /scont|rib|risc/)
        const ivaKey   = findColKey(item, /^iva$|aliq|%\s*iva|iva\s*%/)
        const pu = parseItalianNum(item[prezKey || ''])
        const sconto = parseItalianNum(item[sconKey || ''])
        const qta = Math.round(parseItalianNum(item[qtaKey || ''])) || 1
        return {
          descrizione: item[descrKey || ''] || '',
          quantita: qta,
          prezzo_listino: pu,
          prezzo_unitario: pu,
          iva_percentuale: 0,
          costo_spedizione: 0,
        }
      }))
    }
    setIsAiPreviewModalOpen(false)
    setAiPreviewData(null)
    message.success('Dati del preventivo importati nella form')
  }

  const handleViewAttachment = async () => {
    if (!editingRda?.id) return

    try {
      setIsViewingAttachmentModal(true)
      const response = await axiosInstance.get(
        `/purchase-requests/${editingRda.id}/attachment/download?download=false`,
        { responseType: 'blob' }
      )

      // Crea URL dal blob
      const url = URL.createObjectURL(response.data)
      setAttachmentBlobUrl(url)
    } catch (error: any) {
      setIsViewingAttachmentModal(false)
      message.error('Errore nel caricamento dell\'allegato: ' + (error.response?.data?.detail || error.message || 'Errore sconosciuto'))
    }
  }

  const handleDownloadFromModal = async () => {
    if (!editingRda?.id) return

    try {
      const response = await axiosInstance.get(
        `/purchase-requests/${editingRda.id}/attachment/download?download=true`,
        { responseType: 'blob' }
      )

      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `allegato_rda_${editingRda.codice}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 100)
      message.success('Download avviato')
    } catch (error: any) {
      message.error('Errore durante il download: ' + (error.message || 'Errore sconosciuto'))
    }
  }

  const handleZoomIn = () => {
    setAttachmentZoom(prev => Math.min(prev + 10, 300))
  }

  const handleZoomOut = () => {
    setAttachmentZoom(prev => Math.max(prev - 10, 50))
  }

  const handleResetZoom = () => {
    setAttachmentZoom(100)
  }

  const handleSaveRda = async (values: any) => {
    if (!editingRda) return

    try {
      const isNewRda = editingRda.id === 0
      // Validazione: fornitore obbligatorio
      if (!values.fornitore) {
        message.error('Il fornitore è obbligatorio. Seleziona un fornitore prima di salvare.')
        return
      }

      // Validazione: ogni item deve avere descrizione
      const invalidItems = rdaItems.filter((item: any) => !item.descrizione || item.descrizione.trim() === '')
      if (invalidItems.length > 0) {
        message.error(`${invalidItems.length} articoli hanno descrizione vuota. Compila tutti i campi.`)
        return
      }
      
      /**
       * Le date che l'utente compila a mano, in un posto solo.
       *
       * CREATE e UPDATE costruiscono il payload separatamente, ed e' esattamente
       * cosi' che e' nato il difetto: la creazione non mandava queste due, chi
       * le compilava nella modale le ritrovava vuote riaprendo la RDO. Le altre
       * tre date (richiesta, approvazione, ricezione) restano nel solo ramo
       * UPDATE perche' alla creazione le scrive il server.
       *
       * ⚠️ I due rami vanno tenuti allineati a mano. Non li unifico oltre
       * questo: la modale viene sostituita in R5, e un'astrazione costruita
       * adesso sarebbe da buttare insieme a lei.
       */
      const dateCompilate = {
        data_ordine: values.data_ordine?.format('YYYY-MM-DDTHH:mm:ss'),
        data_consegna_prevista: values.data_consegna_prevista?.format('YYYY-MM-DDTHH:mm:ss'),
      }

      if (isNewRda) {
        // CREATE nuova RDA
        const payload = {
          ...dateCompilate,
          work_order_id: editingRda.work_order_id,
          intervention_id: editingRda.intervention_id,
          descrizione: values.descrizione || 'RDO da intervento',
          fornitore: values.fornitore,
          costo_stimato_totale: totaleScontato,
          supplier_id: values.supplier_id,
          items: rdaItems.map((item: any) => ({
            descrizione: item.descrizione,
            quantita: item.quantita,
            prezzo_unitario_stimato: item.prezzo_unitario || 0,
            sconto_percentuale: item.sconto_percentuale || 0,
            iva_percentuale: item.iva_percentuale || 0,
            costo_spedizione: item.costo_spedizione || 0,
            offerta_source: item.selectedOffer?.source ?? null,
            offerta_source_url: item.selectedOffer?.source_url ?? null,
            offerta_titolo: item.selectedOffer?.titolo ?? null,
            offerta_fornitore: item.selectedOffer?.fornitore ?? null,
            offerta_immagine_url: item.selectedOffer?.immagine_url ?? null,
            offerta_ai_score: item.selectedOffer?.ai_score ?? null,
            offerta_ai_note: item.selectedOffer?.ai_note ?? null,
          })),
          note: values.note,
        }

        await createRda(payload)
        message.success('RDO creata con successo')

        // Refetch il dettaglio del work order per mostrare la nuova RDA
        await loadWorkOrderDetail(editingRda.work_order_id)
      } else {
        // UPDATE RDA esistente
        const payload = {
          ...dateCompilate,
          fornitore: values.fornitore,
          data_richiesta: values.data_richiesta?.format('YYYY-MM-DDTHH:mm:ss'),
          data_approvazione: values.data_approvazione?.format('YYYY-MM-DDTHH:mm:ss'),
          data_ricezione: values.data_ricezione?.format('YYYY-MM-DDTHH:mm:ss'),
          costo_stimato_totale: totaleScontato,
          // `id` distingue l'articolo esistente (aggiornato in posto: ricevuto,
          // reso, sostituisce_item_id restano) da quello nuovo (senza id).
          // Senza l'id il backend cancellava e ricreava tutto, e i campi che
          // questo form non gestisce sparivano (difetto del 13/09/2026).
          items: rdaItems.map((item: any) => ({
            id: item.id ?? null,
            descrizione: item.descrizione,
            quantita: item.quantita,
            prezzo_unitario: item.prezzo_unitario || 0,
            sconto_percentuale: item.sconto_percentuale || 0,
            iva_percentuale: item.iva_percentuale || 0,
            costo_spedizione: item.costo_spedizione || 0,
            offerta_source: item.selectedOffer?.source ?? null,
            offerta_source_url: item.selectedOffer?.source_url ?? null,
            offerta_titolo: item.selectedOffer?.titolo ?? null,
            offerta_fornitore: item.selectedOffer?.fornitore ?? null,
            offerta_immagine_url: item.selectedOffer?.immagine_url ?? null,
            offerta_ai_score: item.selectedOffer?.ai_score ?? null,
            offerta_ai_note: item.selectedOffer?.ai_note ?? null,
          })),
          file_allegato_path: editingRda.file_allegato_path,
          note: values.note ?? null,
        }
        await updateRda({
          rdaId: editingRda.id,
          data: payload,
        })
        message.success('RDO aggiornata con successo')
        
        // Refetch work order detail to update UI with saved changes
        await loadWorkOrderDetail(editingRda.work_order_id)
      }
      
      closeRdaModal()
    } catch (error: any) {
      console.error('🔴 SAVE FAILED:', error)
      console.error('Error message:', error.message)
      console.error('Error response:', error.response?.data)
      
      // Parse validation errors if array
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        console.error('🔴 VALIDATION ERRORS:')
        error.response.data.detail.forEach((err: any, idx: number) => {
          console.error(`  [${idx}]`, err)
        })
        const errorMessages = error.response.data.detail.map((err: any) => err.msg || err.message || JSON.stringify(err)).join(' | ')
        message.error(`Validazione fallita: ${errorMessages}`)
      } else {
        message.error('Errore nel salvataggio della RDO')
      }
    }
  }

  const handleDeleteRda = async (rda: PurchaseRequest) => {
    Modal.confirm({
      title: 'Conferma eliminazione',
      content: 'Sei sicuro di voler eliminare questa RDO?',
      okText: 'Sì',
      cancelText: 'No',
      onOk: async () => {
        try {
          // Usa direttamente il work_order_id dalla RDA passata
          const woId = rda.work_order_id
          
          await deleteRda(rda.id)
          message.success('RDO eliminata')
          
          // Refetcha il work order per mostrare l'aggiornamento
          if (woId) {
            await loadWorkOrderDetail(woId)
          } else {
          }
        } catch (error: any) {
          console.error('🔴 DELETE FAILED:', error)
          console.error('Error message:', error.message)
          console.error('Error response:', error.response?.data)
          message.error(`Errore nell'eliminazione: ${error.response?.data?.detail || error.message}`)
          // Ricarica il work order per sincronizzare lo stato
          const woId = rda.work_order_id
          if (woId) {
            await loadWorkOrderDetail(woId)
          }
        }
      },
    })
  }

  const handleNuovaRdoDaRda = (intervention: Intervention, rda: any | null, workOrderId: number) => {
    const prePopulatedItems: RDAItem[] = (rda?.items ?? []).map((item: any) => ({
      descrizione: item.descrizione,
      quantita: 1,
      prezzo_unitario: 0,
      sconto_percentuale: 0,
      iva_percentuale: 0,
      costo_spedizione: 0,
    }))
    const newRdo: PurchaseRequest = {
      id: 0,
      codice: '',
      work_order_id: workOrderId,
      intervention_id: intervention.id,
      purchase_request_status_id: 1,
      descrizione: intervention.descrizione_intervento ?? '',
      costo_stimato_totale: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      data_richiesta: new Date().toISOString(),
      items: [],
      richiesto_da_id: 0,
      fornitore: '',
      supplier_id: undefined,
    }
    setEditingRda(newRdo)
    setRdaItems(prePopulatedItems)
    rdaForm.resetFields()
    rdaForm.setFieldsValue({
      descrizione: intervention.descrizione_intervento ?? '',
    })
    setIsRdaModalOpen(true)
  }

  /**
   * PONTE PROVVISORIO — apre la creazione offerta arrivando da /acquisti.
   *
   * Il dettaglio acquisti non puo' aprire questa modale: vive dentro questa
   * pagina, legata ai suoi stati. Finche' la creazione dell'offerta non viene
   * riscritta dentro il contenitore nuovo — che e' il piano, non un'ipotesi —
   * il collegamento passa da un parametro nell'URL.
   *
   * Il giorno della riscrittura si cancella questo blocco e il link che lo
   * chiama. Nessuna astrazione in mezzo: sarebbe costruita per essere buttata.
   */
  useEffect(() => {
    const idIntervento = searchParams.get('nuova_offerta_intervento')
    if (!idIntervento) return
    let annullato = false
    ;(async () => {
      try {
        const { data } = await axiosInstance.get(`/acquisti/intervento/${idIntervento}`)
        if (annullato) return
        const i = data.intervento
        handleNuovaRdoDaRda(
          { id: i.intervention_id, descrizione_intervento: i.descrizione_intervento } as Intervention,
          { items: data.lista_spesa },
          i.work_order_id,
        )
      } catch {
        message.error('Impossibile aprire la creazione offerta per questo intervento')
      }
    })()
    return () => { annullato = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Card loading={workOrdersLoading}>
          <div style={{ marginBottom: '16px' }}>
            {rdaStatoFiltro && (
              <Alert
                type="info"
                style={{ marginBottom: 12 }}
                message={
                  <span>
                    Filtro attivo: <Typography.Text strong>{rdaStatoFiltro}</Typography.Text>
                    {' — '}solo schede con RDA in questo stato
                  </span>
                }
                action={<Button size="small" onClick={() => navigate('/rda')}>Rimuovi filtro</Button>}
                showIcon
              />
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>Schede Lavoro con Acquisti</h3>
              <Space>
                <AutoRefreshBadge updatedAt={woUpdatedAt || null} />
                <Button icon={<DownloadOutlined />} onClick={expandAll}>
                  Espandi Tutto
                </Button>
                <Button icon={<UploadOutlined />} onClick={collapseAll}>
                  Richiudi Tutto
                </Button>
              </Space>
            </div>
            <Space wrap>
              <Input.Search
                placeholder="Cerca per N. scheda, cliente o targa..."
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value)
                  if (!e.target.value && searchTerm) { setSearchTerm(''); setPage(1) }
                }}
                onSearch={value => { setSearchTerm(value.trim()); setPage(1) }}
                allowClear
                enterButton
                style={{ maxWidth: 380 }}
              />
              <Select
                allowClear
                placeholder="Stato WO"
                style={{ width: 160 }}
                value={woStatoFiltro}
                onChange={v => { setWoStatoFiltro(v); setPage(1) }}
                options={woStatusTypes.map((s: any) => ({ value: s.nome, label: s.nome }))}
              />
              <Select
                allowClear
                placeholder="Stato RDA"
                style={{ width: 180 }}
                value={rdaStatoLocal}
                onChange={v => { setRdaStatoLocal(v); setPage(1) }}
                options={[
                  { value: 'Bozza', label: 'Bozza' },
                  { value: 'In attesa approvazione', label: 'In attesa approvazione' },
                  { value: 'Approvata', label: 'Approvata' },
                  { value: 'Ordinata', label: 'Ordinata' },
                  { value: 'Ricevuta', label: 'Ricevuta' },
                  { value: 'Annullata', label: 'Annullata' },
                  { value: 'Rifiutata', label: 'Rifiutata' },
                ]}
              />
            </Space>
          </div>

          {/* Tab In lavorazione / Riconsegnate */}
          <Tabs
            activeKey={activeTab}
            onChange={key => {
              setActiveTab(key as 'in-lavorazione' | 'riconsegnate')
              setPage(1)
              setExpandedKeys([])
              setWorkOrderDetails(new Map())
              setWorkOrderRdas(new Map())
            }}
            style={{ marginBottom: 0 }}
            items={[
              { key: 'in-lavorazione', label: <span><ToolOutlined style={{ marginRight: 6 }} />In lavorazione</span> },
              { key: 'riconsegnate', label: <span><CheckCircleOutlined style={{ marginRight: 6 }} />Riconsegnate</span> },
            ]}
          />

          {/* Collapse con lista work orders */}
          {workOrdersLoading ? (
            <Spin style={{ display: 'block', margin: '24px auto' }} />
          ) : !workOrdersData?.items || workOrdersData.items.length === 0 ? (
            <Empty style={{ margin: '24px 0' }} description="Nessuna scheda lavoro con acquisti" />
          ) : (
            <Collapse
              activeKey={expandedKeys}
              onChange={handleCollapseChange}
              items={workOrdersData.items
                .filter(wo => {
                  // Mostra solo WO con almeno una RDA (nuova entità rdas)
                  if (workOrderRdas.has(wo.id)) {
                    return (workOrderRdas.get(wo.id) ?? []).length > 0
                  }
                  return false
                })
                .map(workOrder => {
                  const woRdas = workOrderRdas.get(workOrder.id) ?? []
                  const woDetail = workOrderDetails.get(workOrder.id)

                  // Computa badge RDO per stato
                  const rdoStatusCounts: Record<string, number> = {}
                  let senzaRdoCount = 0
                  if (woDetail) {
                    const interventionRdaMap: Record<number, any> = {}
                    for (const rda of woRdas) {
                      interventionRdaMap[rda.intervention_id] = rda
                    }
                    for (const intervention of woDetail.interventions ?? []) {
                      const rdos: PurchaseRequest[] = (intervention as any).purchase_requests ?? []
                      const rda = intervention.id != null ? interventionRdaMap[intervention.id] : undefined
                      if ((rda?.items ?? []).length > 0 && rdos.length === 0) {
                        senzaRdoCount++
                      }
                      for (const rdo of rdos) {
                        const nome = ((rdo as any).purchase_request_status_nome ?? '').toLowerCase() || `stato ${rdo.purchase_request_status_id}`
                        rdoStatusCounts[nome] = (rdoStatusCounts[nome] ?? 0) + 1
                      }
                    }
                  }

                  return ({
                  key: workOrder.id.toString(),
                  label: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '16px' }}>
                      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
                        <LabeledValue label="Scheda" value={workOrder.numero_scheda} accent />
                        <LabeledValue label="Targa" value={workOrder.vehicle_targa} mono />
                        <LabeledValue
                          label="Veicolo"
                          value={[workOrder.vehicle_marca, workOrder.vehicle_modello, workOrder.vehicle_anno].filter(Boolean).join(' ') || undefined}
                        />
                        <LabeledValue label="Cliente" value={workOrder.customer_nome} />
                      </div>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexShrink: 0 }}>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.6px', lineHeight: 1, marginBottom: '4px' }}>
                            Stato RDO
                          </div>
                          <Space size={4} wrap>
                            {senzaRdoCount > 0 && (
                              <Tag color="error" style={{ marginRight: 0, fontSize: '11px' }}>
                                Senza RDO ×{senzaRdoCount}
                              </Tag>
                            )}
                            {RDO_STATUS_ORDER.filter(nome => rdoStatusCounts[nome] > 0).map(nome => (
                              <Tag key={nome} color={RDO_STATUS_COLORS[nome]} style={{ marginRight: 0, fontSize: '11px' }}>
                                {RDO_STATUS_LABELS[nome]} ×{rdoStatusCounts[nome]}
                              </Tag>
                            ))}
                            {senzaRdoCount === 0 && RDO_STATUS_ORDER.every(nome => !rdoStatusCounts[nome]) && (
                              <span style={{ fontSize: '12px', color: token.colorTextQuaternary }}>—</span>
                            )}
                          </Space>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.6px', lineHeight: 1, marginBottom: '4px' }}>
                            Stato scheda lavoro
                          </div>
                          <Tag
                            color={WO_STATUS_COLORS[workOrder.stato?.toLowerCase()] ?? 'default'}
                            style={{ marginRight: 0, fontSize: '11px' }}
                          >
                            {workOrder.stato}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  ),
                  children: (
                    <RenderWorkOrderDetail
                      details={workOrderDetails.get(workOrder.id)}
                      isLoading={loadingDetails.has(workOrder.id)}
                      rdas={workOrderRdas.get(workOrder.id) ?? []}
                      onEditRda={handleEditRda}
                      onDeleteRda={handleDeleteRda}
                      onNuovaRdo={(intervention, rda) => handleNuovaRdoDaRda(intervention, rda, workOrder.id)}
                    />
                  ),
                })})}
            />
          )}
          {!workOrdersLoading && workOrdersData && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={workOrdersData.total}
                onChange={(p) => setPage(p)}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onShowSizeChange={(_: number, size: number) => { setPageSize(size); setPage(1) }}
                showTotal={(total: number) => `Totale ${total} schede`}
              />
            </div>
          )}
        </Card>
      </div>

      {/* Modal Modifica RDA */}
      <Modal
        title={
          editingRda?.id === 0
            ? editingRda?.codice
              ? `[${editingRda.codice}] Nuova Richiesta di Offerta${getInterventionDescription() ? ` — ${getInterventionDescription()}` : ''}`
              : getInterventionDescription()
                ? `Nuova Richiesta di Offerta — ${getInterventionDescription()}`
                : 'Nuova Richiesta di Offerta'
            : editingRda?.codice
              ? `[${editingRda.codice}] Modifica Richiesta di Offerta${getInterventionDescription() ? ` — ${getInterventionDescription()}` : ''}`
              : editingRda && getInterventionDescription()
                ? `Modifica Richiesta di Offerta — ${getInterventionDescription()}`
                : 'Modifica Richiesta di Offerta'
        }
        open={isRdaModalOpen}
        onCancel={closeRdaModal}
        width="min(960px, 96vw)"
        styles={{ body: { padding: '16px 20px 8px' } }}
        footer={[
          <Button key="cancel" onClick={closeRdaModal}>Annulla</Button>,
          <Button key="submit" type="primary" onClick={() => rdaForm.submit()} disabled={isFieldsDisabled}>
            Salva
          </Button>,
        ]}
      >
        <Form form={rdaForm} layout="vertical" onFinish={handleSaveRda}>

          {/* Allegati — barra orizzontale compatta */}
          {editingRda && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '7px 10px', backgroundColor: token.colorFillAlter, borderRadius: '6px', border: `1px solid ${token.colorBorderSecondary}` }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Allegato</span>
              <Upload
                maxCount={1}
                beforeUpload={handleUploadRdaFile}
                accept=".pdf,.txt,.csv,image/jpeg,image/png,image/gif,image/webp"
                showUploadList={false}
                disabled={isUploadingFile || isFieldsDisabled}
              >
                <Button loading={isUploadingFile} icon={<UploadOutlined />} disabled={!!editingRda.file_allegato_path || isFieldsDisabled} size="small">
                  Carica file
                </Button>
              </Upload>
              {editingRda.file_allegato_path ? (
                <>
                  <span style={{ fontSize: '11px', color: token.colorSuccess, fontWeight: 500 }}>✓ File caricato</span>
                  <Button type="text" icon={<DownloadOutlined />} onClick={handleViewAttachment} size="small">Vedi</Button>
                  <Button type="text" icon={<RobotOutlined />} size="small" loading={isAnalyzing} onClick={handleAnalyzeWithAI} style={{ color: '#722ed1' }}>AI</Button>
                  <Popconfirm title="Eliminare l'allegato?" onConfirm={handleDeleteRdaFile} okText="Sì" cancelText="No">
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" disabled={isFieldsDisabled} />
                  </Popconfirm>
                </>
              ) : (
                <span style={{ fontSize: '11px', color: token.colorTextQuaternary }}>Nessun file</span>
              )}
            </div>
          )}

          {/* Workflow */}
          {editingRda && editingRda.id !== 0 && (
            <div style={{ marginBottom: '12px', padding: '10px 12px', backgroundColor: token.colorInfoBg, borderRadius: '6px', border: `1px solid ${token.colorInfoBorder}` }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: token.colorInfoText, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Stato workflow</div>
              <RDAStateTransition
                rdaId={editingRda.id}
                currentStatusId={editingRda.purchase_request_status_id}
                currentStatusName={(editingRda as any).purchase_request_status_nome ?? `Stato ${editingRda.purchase_request_status_id}`}
                motivazioneRifiuto={editingRda.motivazione_rifiuto}
                commentoStato={(editingRda as any).commento_stato}
                dataConsegnaPrevista={(editingRda as any).data_consegna_prevista}
                motivoDerogaData={(editingRda as any).motivo_deroga_data}
                onStateChange={(newStatusId, newStatusName, commentoStato) => {
                  setEditingRda(prev => prev ? {
                    ...prev,
                    purchase_request_status_id: newStatusId,
                    purchase_request_status_nome: newStatusName,
                    commento_stato: commentoStato ?? null,
                  } as any : prev)
                  if (editingRda.work_order_id) {
                    loadWorkOrderDetail(editingRda.work_order_id)
                    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
                  }
                }}
              />
            </div>
          )}

{/* Note */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>Note</div>
            <Form.Item name="note" noStyle>
              <Input.TextArea
                rows={1}
                autoSize={{ minRows: 1, maxRows: 3 }}
                placeholder="Annotazioni libere sulla RDO..."
                disabled={isFieldsDisabled}
                style={{ fontSize: '12px' }}
              />
            </Form.Item>
          </div>

{/* Avviso protezione */}
          {showFormErrorMessage && (
            <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: token.colorWarningBg, border: `1px solid ${token.colorWarningBorder}`, borderRadius: '6px', fontSize: '12px', color: token.colorWarningText }}>
              I campi sono protetti. Solo gli approvanti possono modificare una RDA dopo l'approvazione.
            </div>
          )}

          {/* Informazioni — griglia compatta 5 colonne */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
              Informazioni
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: '8px', alignItems: 'end' }}>
              {/* Fornitore — visibile solo con suppliers.view */}
              {canViewSuppliers && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: token.colorTextSecondary, marginBottom: '3px' }}>Fornitore</div>
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="fornitore" noStyle>
                      <Select
                        size="small"
                        placeholder="Seleziona..."
                        disabled={isFieldsDisabled}
                        options={(suppliersData?.items || []).map(s => ({ label: s.nome, value: s.nome }))}
                        style={{ flex: 1 }}
                      />
                    </Form.Item>
                    <Tooltip title="Nuovo fornitore">
                      <Button size="small" icon={<PlusOutlined />} onClick={() => setIsSupplierModalOpen(true)} />
                    </Tooltip>
                  </Space.Compact>
                </div>
              )}
              {/* Data Offerta */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: token.colorTextSecondary, marginBottom: '3px' }}>Data offerta</div>
                <Form.Item name="data_richiesta" noStyle>
                  <DatePicker size="small" format="DD/MM/YYYY" disabled={isFieldsDisabled} style={{ width: '100%' }} />
                </Form.Item>
              </div>
              {/* Data Ordine */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: token.colorTextSecondary, marginBottom: '3px' }}>Data ordine</div>
                <Form.Item name="data_ordine" noStyle>
                  <DatePicker size="small" format="DD/MM/YYYY" disabled={isFieldsDisabled} style={{ width: '100%' }} />
                </Form.Item>
              </div>
              {/* Data Consegna Prevista */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: token.colorTextSecondary, marginBottom: '3px' }}>Consegna prevista</div>
                <Form.Item name="data_consegna_prevista" noStyle>
                  <DatePicker
                    size="small"
                    format="DD/MM/YYYY"
                    disabled={isFieldsDisabled}
                    style={{ width: '100%' }}
                    disabledDate={(current) =>
                      editingRda?.purchase_request_status_id === 5
                        ? current && current.isBefore(dayjs().startOf('day'))
                        : false
                    }
                    onChange={(date) =>
                      setEditingRda(prev => prev
                        ? { ...prev, data_consegna_prevista: date ? date.toISOString() : null } as any
                        : prev)
                    }
                  />
                </Form.Item>
              </div>
              {/* Date auto (approvazione + ricezione + creazione) */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 600, color: token.colorTextSecondary, marginBottom: '3px' }}>Date automatiche</div>
                <div style={{ fontSize: '11px', color: token.colorTextTertiary, lineHeight: '18px', padding: '3px 0' }}>
                  {editingRda?.data_approvazione
                    ? <div>Appr: <span style={{ color: token.colorText }}>{dayjs(editingRda.data_approvazione).format('DD/MM/YY')}</span></div>
                    : <div style={{ color: token.colorTextQuaternary }}>Appr: —</div>
                  }
                  {editingRda && editingRda.created_at
                    ? <div>Creaz: <span style={{ color: token.colorText }}>{dayjs(editingRda.created_at).format('DD/MM/YY')}</span></div>
                    : null
                  }
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: '10px 0 8px' }} />

          {/* Articoli — tabella stile Excel con colonne allineate */}
          {(() => {
            // 9 colonne: Desc | Qtà | Prezzo u. | Tot. listino | Sconto % | Tot. scontato | Sped. | Cerca | Del
            const COLS = 'minmax(0,1fr) 52px 100px 92px 80px 96px 76px 28px 28px'
            const B = `1px solid ${token.colorBorderSecondary}`
            const hStyle: React.CSSProperties = {
              fontSize: '10px', fontWeight: 700,
              color: token.colorTextSecondary,
              backgroundColor: token.colorFillSecondary,
              borderRight: B, borderBottom: `2px solid ${token.colorBorder}`,
              padding: '5px 7px', display: 'flex', alignItems: 'center',
            }
            const hR: React.CSSProperties = { ...hStyle, justifyContent: 'flex-end' }
            const cell: React.CSSProperties = {
              borderRight: B, borderBottom: B, padding: '3px 6px',
              display: 'flex', alignItems: 'center',
            }
            const cellR: React.CSSProperties = { ...cell, justifyContent: 'flex-end' }
            const ftStyle: React.CSSProperties = {
              borderRight: B, borderTop: `2px solid ${token.colorBorder}`,
              padding: '5px 7px', display: 'flex', alignItems: 'center',
              backgroundColor: token.colorFillAlter,
            }
            const ftR: React.CSSProperties = { ...ftStyle, justifyContent: 'flex-end' }

            const vehicleCtx = (() => {
              const wo = editingRda ? workOrderDetails.get(editingRda.work_order_id) : undefined
              if (!wo) return undefined
              const parts: string[] = []
              if (wo.vehicle_marca) parts.push(wo.vehicle_marca)
              if (wo.vehicle_modello) parts.push(wo.vehicle_modello)
              if (wo.vehicle_anno) parts.push(String(wo.vehicle_anno))
              if ((wo as any).vehicle_codice_motore) parts.push(`motore: ${(wo as any).vehicle_codice_motore}`)
              return parts.length ? parts.join(' ') : undefined
            })()

            return (
              <div style={{ marginBottom: '4px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' }}>
                  Articoli
                </div>

                <div style={{ border: B, borderRadius: '4px', overflow: 'hidden' }}>

                  {/* Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: COLS }}>
                    <div style={{ ...hStyle, borderRight: B }}>Descrizione</div>
                    <div style={hR}>Qtà</div>
                    <div style={hR}>Prezzo u.</div>
                    <div style={hR}>Tot. listino</div>
                    <div style={hR}>Sconto %</div>
                    <div style={hR}>Tot. scontato</div>
                    <div style={hR}>Sped.</div>
                    <div style={{ ...hStyle, borderRight: 'none', gridColumn: 'span 2' }} />
                  </div>

                  {/* Righe */}
                  {rdaItems.length === 0 ? (
                    <Empty description="Nessun articolo" style={{ margin: '12px 0' }} />
                  ) : (
                    <div ref={itemsContainerRef} style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      {rdaItems.map((item, idx) => {
                        const rowBg = idx % 2 === 0 ? token.colorBgContainer : token.colorFillAlter
                        const rCell: React.CSSProperties = { ...cell, backgroundColor: rowBg }
                        const rCellR: React.CSSProperties = { ...cellR, backgroundColor: rowBg }
                        const isLast = idx === rdaItems.length - 1
                        const lastBorder = isLast ? 'none' : B
                        return (
                          <div key={`item-${idx}`}>
                            {item.selectedOffer && (
                              <div style={{ padding: '2px 8px 0', backgroundColor: rowBg, borderBottom: B }}>
                                <OfferPreviewBanner result={item.selectedOffer} onRemove={() => updateItem(idx, 'selectedOffer', undefined)} />
                              </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: COLS }}>
                              <div style={{ ...rCell, borderBottom: lastBorder }}>
                                <Input
                                  size="small"
                                  value={item.descrizione}
                                  onChange={(e) => updateItem(idx, 'descrizione', e.target.value)}
                                  placeholder="Descrizione articolo"
                                  style={{ border: 'none', boxShadow: 'none', backgroundColor: 'transparent', padding: '0 2px' }}
                                />
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder }}>
                                <InputNumber
                                  size="small" controls={false}
                                  style={{ width: '100%', border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
                                  value={item.quantita}
                                  onChange={(val) => updateItem(idx, 'quantita', val || 0)}
                                  min={0} step={1} precision={0}
                                />
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder }}>
                                <InputNumber
                                  size="small" controls={false}
                                  style={{ width: '100%', border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
                                  value={item.prezzo_unitario}
                                  onChange={(val) => updateItem(idx, 'prezzo_unitario', val || 0)}
                                  min={0} precision={2} prefix="€"
                                />
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder, fontSize: '12px', fontWeight: 500, color: token.colorText }}>
                                {formatEuro(calcTotaleListinoItem(item))}
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder }}>
                                <input
                                  type="number" min={0} max={100} step={0.1}
                                  className="rda-sconto-input"
                                  value={item.sconto_percentuale ?? 0}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value)
                                    updateItem(idx, 'sconto_percentuale', isNaN(v) ? 0 : Math.min(100, Math.max(0, v)))
                                  }}
                                  style={{ width: '52px', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right', fontSize: '12px', color: token.colorText, padding: 0, fontFamily: 'inherit' }}
                                />
                                <span style={{ fontSize: '11px', color: token.colorTextSecondary, marginLeft: '2px' }}>%</span>
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder, fontSize: '12px', fontWeight: 700, color: token.colorPrimary }}>
                                {formatEuro(calcTotaleScontatoItem(item))}
                              </div>
                              <div style={{ ...rCellR, borderBottom: lastBorder }}>
                                <InputNumber
                                  size="small" controls={false}
                                  style={{ width: '100%', border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }}
                                  value={item.costo_spedizione || undefined}
                                  onChange={(val) => updateItem(idx, 'costo_spedizione', val || 0)}
                                  min={0} precision={2} prefix="€"
                                />
                              </div>
                              <div style={{ ...rCell, borderBottom: lastBorder, justifyContent: 'center' }}>
                                <MarketSearch
                                  itemId={item.id}
                                  defaultQuery={item.descrizione}
                                  vehicleContext={vehicleCtx}
                                  iconOnly
                                  onSelectOffer={(price, _supplier, _url, result) => {
                                    if (price != null) updateItem(idx, 'prezzo_unitario', price)
                                    updateItem(idx, 'quantita', 1)
                                    updateItem(idx, 'selectedOffer', result)
                                    rdaForm.setFieldValue('fornitore', 'Ricerca AI')
                                  }}
                                />
                              </div>
                              <div style={{ ...rCell, borderRight: 'none', borderBottom: lastBorder, justifyContent: 'center' }}>
                                <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeItem(idx)} style={{ padding: '0 2px' }} />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Footer totali — stessa griglia, valori allineati con le colonne */}
                  <div style={{ display: 'grid', gridTemplateColumns: COLS }}>
                    {/* col 1: pulsante aggiungi */}
                    <div style={{ ...ftStyle }}>
                      <Button type="dashed" icon={<PlusOutlined />} onClick={addItem} size="small">
                        Aggiungi articolo
                      </Button>
                    </div>
                    {/* col 2 (Qtà): vuota */}
                    <div style={ftStyle} />
                    {/* col 3 (Prezzo u.): label "Totali" */}
                    <div style={{ ...ftR, fontSize: '10px', fontWeight: 700, color: token.colorTextSecondary }}>
                      Totali
                    </div>
                    {/* col 4 (Tot. listino): valore */}
                    <div style={{ ...ftR, fontSize: '13px', fontWeight: 700, color: token.colorText }}>
                      {formatEuro(totaleListino)}
                    </div>
                    {/* col 5 (Sconto %): vuota */}
                    <div style={ftStyle} />
                    {/* col 6 (Tot. scontato): valore */}
                    <div style={{ ...ftR, fontSize: '13px', fontWeight: 700, color: token.colorPrimary }}>
                      {formatEuro(totaleScontatoBase)}
                    </div>
                    {/* col 7 (Sped.): totale spedizione */}
                    <div style={{ ...ftR, fontSize: '12px', fontWeight: 600, color: token.colorText }}>
                      {formatEuro(totaleSped)}
                    </div>
                    {/* col 8-9: vuote */}
                    <div style={{ ...ftStyle, gridColumn: 'span 2', borderRight: 'none' }} />
                  </div>

                </div>
              </div>
            )
          })()}

        </Form>
      </Modal>

      {/* Modal Visualizza Allegato */}
      <Modal
        title="Visualizza Allegato"
        open={isViewingAttachmentModal}
        onCancel={() => {
          setIsViewingAttachmentModal(false)
        }}
        width="90vw"
        height="90vh"
        style={{ top: 20 }}
        footer={[
          <Button
            key="zoom-out"
            type="default"
            size="small"
            icon={<ZoomOutOutlined />}
            onClick={handleZoomOut}
            disabled={attachmentZoom <= 50}
          >
            Zoom -
          </Button>,
          <span key="zoom-level" style={{ margin: '0 12px', fontSize: '12px', color: token.colorTextSecondary }}>
            {attachmentZoom}%
          </span>,
          <Button
            key="zoom-in"
            type="default"
            size="small"
            icon={<ZoomInOutlined />}
            onClick={handleZoomIn}
            disabled={attachmentZoom >= 300}
          >
            Zoom +
          </Button>,
          <Button
            key="zoom-reset"
            type="default"
            size="small"
            onClick={handleResetZoom}
          >
            Reset
          </Button>,
          <Button
            key="download"
            type="default"
            size="small"
            onClick={handleDownloadFromModal}
            icon={<DownloadOutlined />}
          >
            Scarica
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsViewingAttachmentModal(false)
            }}
          >
            Chiudi
          </Button>,
        ]}
      >
        {attachmentBlobUrl ? (
          <div style={{
            textAlign: 'center',
            height: 'calc(90vh - 200px)',
            overflow: 'auto',
            padding: '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            backgroundColor: token.colorFillAlter,
            borderRadius: '4px'
          }}>
            <div style={{
              transform: `scale(${attachmentZoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            }}>
              <embed
                src={attachmentBlobUrl}
                type="application/pdf"
                width="800"
                height="600"
                style={{ border: 'none', borderRadius: '4px', backgroundColor: token.colorBgContainer, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                title="Allegato RDA"
              />
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: token.colorTextTertiary }}>
            Caricamento in corso...
          </div>
        )}
      </Modal>

      {/* Modal Aggiungi Fornitore */}
      {isSupplierModalOpen && (
        <SupplierQuickAddModal
          open={isSupplierModalOpen}
          initialNome={supplierModalInitialNome}
          onClose={() => {
            setIsSupplierModalOpen(false)
            setSupplierModalInitialNome('')
            refetchSuppliers()
          }}
          onCreated={(nome) => {
            setAiSupplierResolved(nome)
            setIsSupplierModalOpen(false)
            setSupplierModalInitialNome('')
            refetchSuppliers()
          }}
        />
      )}

      {/* Modal Invia RDA in Approvazione */}
      <Modal
        title="Invia RDA in Approvazione?"
        open={isSubmitApprovalModalOpen}
        onOk={async () => {
          if (!editingRda) return
          try {
            let rdaId = editingRda.id

            // Se la RDA non è ancora salvata (id=0), salvala prima
            if (!rdaId) {
              const formValues = rdaForm.getFieldsValue()
              const result = await createRda({
                work_order_id: editingRda.work_order_id,
                intervention_id: editingRda.intervention_id || undefined,
                descrizione: formValues.descrizione || 'RDO',
                fornitore: formValues.fornitore,
                costo_stimato_totale: 0,
                items: rdaItems.map((item: any) => ({
                  descrizione: item.descrizione,
                  quantita: item.quantita,
                  prezzo_unitario_stimato: item.prezzo_unitario || 0,
                  iva_percentuale: item.iva_percentuale || 0,
                  costo_spedizione: item.costo_spedizione || 0,
                })),
                note: formValues.note ?? null,
              })
              rdaId = result.id
            }

            await axiosInstance.patch(`/purchase-requests/${rdaId}/submit`, {})
            message.success('RDA inviata per approvazione')
            setIsSubmitApprovalModalOpen(false)
            closeRdaModal()
            if (editingRda.work_order_id) {
              await loadWorkOrderDetail(editingRda.work_order_id)
            }
          } catch (error: any) {
            message.error(error.response?.data?.detail || 'Errore nell\'invio della RDA')
          }
        }}
        onCancel={() => setIsSubmitApprovalModalOpen(false)}
        okText="Sì, invia"
        cancelText="No, rimani in Bozza"
        width="min(500px, 95vw)"
      >
        <p style={{ marginBottom: '12px' }}>
          Vuoi inviare questa RDA per approvazione?
        </p>
        <div style={{ padding: '12px', backgroundColor: token.colorInfoBg, borderRadius: '4px', borderLeft: `3px solid ${token.colorPrimary}` }}>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: token.colorInfoText, fontWeight: '500' }}>
            • Se <strong>SÌ</strong>: la RDA passerà in stato "In attesa approvazione"
          </p>
          <p style={{ margin: '0', fontSize: '13px', color: token.colorInfoText, fontWeight: '500' }}>
            • Se <strong>NO</strong>: la RDA rimarrà in stato "Bozza" e potrai modificarla prima di inviarla
          </p>
        </div>
      </Modal>

      {/* Modal Anteprima Dati AI */}
      <Modal
        title={<span><RobotOutlined style={{ color: '#722ed1', marginRight: 8 }} />Dati estratti dal preventivo — Conferma</span>}
        open={isAiPreviewModalOpen}
        onOk={handleApplyAiData}
        onCancel={() => { setIsAiPreviewModalOpen(false); setAiPreviewData(null) }}
        okText="Conferma e Popola la Form"
        cancelText="Annulla"
        width={800}
        okButtonProps={{
          style: { background: '#722ed1', borderColor: '#722ed1' },
          disabled: !!(aiPreviewData?.fornitore && !aiSupplierResolved),
        }}
      >
        {aiPreviewData && (
          <div>
            <Alert
              message="Verifica i dati estratti dall'AI prima di confermare. Puoi modificarli nella form dopo l'importazione."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary }}>Fornitore</label>
                <div style={{ marginTop: 4 }}>
                  {!aiPreviewData.fornitore ? (
                    <span style={{ color: token.colorTextQuaternary }}>Non rilevato</span>
                  ) : aiSupplierResolved ? (
                    <Space>
                      <span style={{ padding: '4px 8px', background: token.colorSuccessBg, border: `1px solid ${token.colorSuccessBorder}`, borderRadius: 4, color: token.colorSuccessText, fontWeight: 600 }}>
                        ✓ {aiSupplierResolved}
                      </span>
                    </Space>
                  ) : (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Alert
                        message={<>Fornitore <strong>"{aiPreviewData.fornitore}"</strong> non presente in anagrafica</>}
                        type="warning"
                        showIcon
                        style={{ padding: '4px 8px' }}
                      />
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setSupplierModalInitialNome(aiPreviewData.fornitore)
                          setIsSupplierModalOpen(true)
                        }}
                      >
                        Crea Fornitore
                      </Button>
                    </Space>
                  )}
                </div>
              </Col>
              <Col span={12}>
                <label style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary }}>Totale Stimato</label>
                <div style={{ padding: '6px 8px', background: token.colorFillAlter, borderRadius: 4, marginTop: 4, fontWeight: 600 }}>
                  {aiPreviewData.costo_stimato_totale != null
                    ? `€ ${Number(aiPreviewData.costo_stimato_totale).toFixed(2)}`
                    : <span style={{ color: token.colorTextQuaternary }}>Non rilevato</span>}
                </div>
              </Col>
            </Row>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: token.colorTextSecondary }}>Descrizione</label>
              <div style={{ padding: '6px 8px', background: token.colorFillAlter, borderRadius: 4, marginTop: 4 }}>
                {aiPreviewData.descrizione || <span style={{ color: token.colorTextQuaternary }}>Non rilevata</span>}
              </div>
            </div>
            <Divider style={{ margin: '12px 0' }}>Articoli rilevati ({aiPreviewData.items?.length || 0})</Divider>
            {Array.isArray(aiPreviewData.items) && aiPreviewData.items.length > 0 ? (
              <Table
                size="small"
                pagination={false}
                dataSource={aiPreviewData.items.map((item: any, i: number) => ({ ...item, key: i }))}
                columns={(aiPreviewData.colonne || Object.keys(aiPreviewData.items[0] || {})).map((col: string) => ({
                  title: col,
                  dataIndex: col,
                  key: col,
                  ellipsis: true,
                  render: (v: any) => (v === null || v === undefined || v === '') ? <span style={{ color: token.colorTextQuaternary }}>—</span> : String(v),
                }))}
                scroll={{ x: true, y: 250 }}
              />
            ) : (
              <Empty description="Nessun articolo rilevato" />
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

// Componente per renderizzare il contenuto di ogni accordion
interface RenderWorkOrderDetailProps {
  details?: WorkOrder
  isLoading: boolean
  rdas: any[]
  onEditRda: (rda: PurchaseRequest) => void
  onDeleteRda: (rda: PurchaseRequest) => void
  onNuovaRdo: (intervention: Intervention, rda: any | null) => void
}

const RenderWorkOrderDetail: React.FC<RenderWorkOrderDetailProps> = ({
  details,
  isLoading,
  rdas,
  onEditRda,
  onDeleteRda,
  onNuovaRdo,
}) => {
  const { token } = theme.useToken()
  if (isLoading) {
    return <Spin size="small" />
  }

  if (!details) {
    return <Empty description="Nessun dato disponibile" />
  }

  const allInterventions: Intervention[] = details.interventions ?? []

  // Mappa intervention_id → rda (nuova entità)
  const interventionRdaMap: Record<number, any> = {}
  for (const rda of rdas) {
    interventionRdaMap[rda.intervention_id] = rda
  }

  if (allInterventions.length === 0) {
    return <Empty description="Nessun intervento in questa scheda" />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 0' }}>
      {allInterventions.map((intervention: Intervention) => {
        const rda = interventionRdaMap[intervention.id]
        const rdaItems: any[] = rda?.items ?? []
        const hasRdaItems = rdaItems.length > 0
        const rdoList: PurchaseRequest[] = (intervention as any).purchase_requests ?? []

        return (
          <div key={`int-${intervention.id}`} style={{ paddingLeft: '10px', borderLeft: `2px solid ${hasRdaItems ? '#adc6ff' : '#e8e8e8'}` }}>
            {/* Header intervento */}
            <div style={{ fontSize: '13px', fontWeight: 600, color: hasRdaItems ? '#1d3557' : '#8c8c8c', marginBottom: '6px' }}>
              #{intervention.progressivo} — {intervention.descrizione_intervento}
            </div>

            <div style={{ marginLeft: '8px' }}>
              {/* Articoli RDA o messaggio */}
              {hasRdaItems ? (
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: token.colorPrimary, display: 'block', marginBottom: 2 }}>
                    Articoli da acquistare (RDA):
                  </span>
                  {rdaItems.map((item: any) => (
                    <div key={item.id} style={{ fontSize: 11, color: token.colorTextSecondary }}>· {item.descrizione}</div>
                  ))}
                  {rda?.note && (
                    <div style={{
                      marginTop: 6,
                      padding: '6px 10px',
                      background: token.colorWarningBg,
                      border: `1px solid ${token.colorWarningBorder}`,
                      borderRadius: token.borderRadiusSM,
                      fontSize: 12,
                      fontWeight: 700,
                      color: token.colorWarning,
                      lineHeight: 1.5,
                    }}>
                      📝 {rda.note}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: '11px', color: token.colorTextQuaternary, fontStyle: 'italic' }}>
                    Nessuna RDA — aggiungila dalla scheda lavoro
                  </span>
                </div>
              )}

              {/* Lista RDO */}
              {rdoList.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                  {rdoList.map((rdo: PurchaseRequest) => (
                    <div
                      key={`rdo-${rdo.id}`}
                      onClick={() => onEditRda(rdo)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '5px 8px', backgroundColor: token.colorFillAlter, borderRadius: '4px',
                        fontSize: '12px', gap: '8px', cursor: 'pointer', transition: 'background 0.15s',
                        border: `1px solid ${token.colorBorderSecondary}`,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = token.colorInfoBg }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = token.colorFillAlter }}
                    >
                      <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: token.colorPrimary, minWidth: '110px', fontSize: '11px' }}>{rdo.codice}</span>
                        <Tag color={getStatusDisplay(rdo.purchase_request_status_id, (rdo as any).purchase_request_status_nome).color} style={{ fontSize: '10px', padding: '1px 6px', margin: 0 }}>
                          {getStatusDisplay(rdo.purchase_request_status_id, (rdo as any).purchase_request_status_nome).nome}
                        </Tag>
                        <TagArticoliResi quanti={(rdo as any).articoli_resi} compatto />
                        {rdo.fornitore && <span style={{ color: token.colorTextSecondary, fontSize: '11px' }}>{rdo.fornitore}</span>}
                        {rdo.costo_stimato_totale ? <span style={{ color: token.colorTextTertiary, fontSize: '11px' }}>{formatEuro(Number(rdo.costo_stimato_totale))}</span> : null}
                      </div>
                      <Button
                        danger size="small" icon={<DeleteOutlined />}
                        onClick={(e) => { e.stopPropagation(); onDeleteRda(rdo) }}
                        style={{ fontSize: '10px', padding: '1px 6px', height: '22px' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Bottone Nuova RDO — sempre visibile */}
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={(e) => { e.stopPropagation(); onNuovaRdo(intervention, rda ?? null) }}
                style={{ fontSize: '11px' }}
              >
                Nuova RDO
              </Button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// Componente per aggiungere rapidamente un fornitore
interface SupplierQuickAddModalProps {
  open: boolean
  onClose: () => void
  initialNome?: string
  onCreated?: (nome: string) => void
}

const SupplierQuickAddModal: React.FC<SupplierQuickAddModalProps> = ({ open, onClose, initialNome, onCreated }) => {
  const [supplierForm] = Form.useForm()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && initialNome) {
      supplierForm.setFieldsValue({ nome: initialNome })
    }
    if (!open) {
      supplierForm.resetFields()
    }
  }, [open, initialNome, supplierForm])

  const handleCreateSupplier = async (values: any) => {
    try {
      setLoading(true)
      await axiosInstance.post('/suppliers/', {
        nome: values.nome,
        acronimo: values.acronimo,
        telefono: values.telefono,
        email: values.email,
        indirizzo: values.indirizzo,
        attivo: true,
      })
      message.success('Fornitore creato con successo')
      if (onCreated) onCreated(values.nome)
      else onClose()
    } catch (error: any) {
      message.error('Errore nella creazione del fornitore')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Aggiungi Fornitore"
      open={open}
      onCancel={onClose}
      onOk={() => supplierForm.submit()}
      confirmLoading={loading}
      width="min(600px, 95vw)"
    >
      <Form
        form={supplierForm}
        layout="vertical"
        onFinish={handleCreateSupplier}
        style={{ marginTop: 24 }}
      >
        <Form.Item
          label="Nome Fornitore"
          name="nome"
          rules={[{ required: true, message: 'Obbligatorio' }]}
        >
          <Input placeholder="Es. AutoParts Italia" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              label="Acronimo"
              name="acronimo"
              rules={[{ max: 20, message: 'Troppo lungo' }]}
            >
              <Input placeholder="Es. API" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Telefono"
              name="telefono"
            >
              <PhoneInput placeholder="Numero di telefono" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: 'email', message: 'Email non valida' }]}
        >
          <Input placeholder="info@fornitore.it" />
        </Form.Item>

        <Form.Item
          label="Indirizzo"
          name="indirizzo"
        >
          <Input placeholder="Via/Piazza" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default PurchaseRequestsPage
